// Foundations
import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

// rxjs
import { finalize } from 'rxjs/operators';

// Services
import { AuthService } from 'src/app/auth/auth.service';
import {
  CognitoService,
  FLAG_ACCT_IN_DD,
} from '../../../services/cognito.service';
import { AnalyticsService } from '../../../services/analytics.service';
import { RegisterService } from 'src/app/services/register.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';

// Components
import { ConfirmDialogComponent } from '../../_share/confirm-dialog/confirm-dialog.component';

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;

@Component({
  selector: 'app-sign-in-form',
  templateUrl: './sign-in-form.component.html',
  styleUrls: ['./sign-in-form.component.scss'],
})
export class SignInFormComponent implements OnInit {
  form: FormGroup;

  /**
   * A flag used to disable signIn button
   * @type {boolean}
   */
  disableBtn = true;

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private authService: AuthService,
    private registerService: RegisterService,
    private cognitoService: CognitoService,
    private _analytics: AnalyticsService,
    private router: Router,
    private route: ActivatedRoute,
    private loadingIndicatorService: LoadingIndicatorService
  ) {
    this.form = this.fb.group({
      email: [
        this.registerService.existingEmail,
        { validators: [Validators.required, Validators.pattern(EMAIL_REGEX)] },
      ],
      password: ['', { validators: [Validators.required] }],
      rememberMe: [false],
    });
  }

  ngOnInit(): void {
    this._analytics.logScreen('signInPage');
    // clear existing email after using it to populate
    this.registerService.existingEmail = '';
    this.form.get('email').valueChanges.subscribe((res) => {
      this.form.patchValue(
        { email: this.form.get('email').value.toLowerCase() },
        { emitEvent: false }
      );
    });
    this.form.valueChanges.subscribe((formValues) => {
      if (
        formValues.email &&
        formValues.password &&
        this.f.email.status !== 'INVALID'
      ) {
        this.disableBtn = false;
      } else {
        this.disableBtn = true;
      }
    });
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  get f(): any {
    return this.form.controls;
  }

  rememberMeToggle(): void {
    this._analytics.logButtonTap('signIn_rememberMeToggle');
  }

  navigateToRegister(): void {
    this._analytics.logLinkTap('signIn_signUp');
  }

  navigateToForgotPassword(): void {
    this._analytics.logLinkTap('signIn_forgotPassword');
  }

  onCancel(): void {
    this.router.navigate(['/']);
  }
  onSubmit(): void {
    this._analytics.logButtonTap('signIn_signIn');
    if (this.form.valid) {
      this.loadingIndicatorService.show();

      const { email, password, rememberMe } = this.form.value;
      this.cognitoService.rememberMe = rememberMe;
      this.cognitoService
        .signIn({ username: email, password })
        .subscribe((signInStatus) => {
          if (signInStatus.result === 'SUCCESS') {
            this.cognitoService
              .getCognitoAttrValue(
                { username: email, password },
                FLAG_ACCT_IN_DD
              )
              .pipe(finalize(() => this.loadingIndicatorService.hide()))
              .subscribe((getStatus) => {
                if (getStatus.result === 'TRUE') {
                  // "Acct in DD" flag is TRUE, navigate to landing page
                  this.router.navigate(['/']);
                } else if (getStatus.result === 'NOT FOUND') {
                  // "Acct in DD" flag is not found, set flag to TRUE and navigate to landing page
                  this.cognitoService
                    .updateCognitoAttrValue(
                      { username: email, password },
                      FLAG_ACCT_IN_DD,
                      'TRUE'
                    )
                    .subscribe((updateStatus) => {
                      if (updateStatus.result === 'SUCCESS') {
                        this.router.navigate(['/']);
                      } else {
                        this.snackBar.open(updateStatus.message, '', {
                          duration: 2000,
                        });
                      }
                    });
                } else {
                  // "Acct in DD" flag is FALSE, navigate to /sign-in/info
                  this.cognitoService.signOut();
                  this.cognitoService.rememberMe = false;
                  this.registerService.updateRegisterFormData({
                    email,
                    password,
                  });
                  this.router.navigate(['./info'], { relativeTo: this.route });
                }
              });
          } else {
            this.loadingIndicatorService.hide();
            // sign-in error
            if (signInStatus.result === 'UserNotConfirmedException') {
              // user not confirmed, navigate to verification code page
              this.registerService.updateRegisterFormData({ email, password });
              this.cognitoService.resendCode(email);
              this.router.navigate(['/register/code']);
            } else {
              // other reasons: user not authorized or incorrect login
              this.dialog.open(ConfirmDialogComponent, {
                panelClass: 'general-dialog',
                data: {
                  title: 'Sign-In Error',
                  content: `Email and Password do not match, please try again.`,
                  okText: 'OK',
                },
                autoFocus: false,
                disableClose: true,
              });
            }
          }
        });
    }
  }
}
